/**
 * Turning a card ID into something a person can read.
 *
 * Parsing goes through `@cards/toolkit` rather than being re-implemented here:
 * the ID format is an implementation detail that multi-deck support has already
 * changed once, and a second parser is a second thing to forget to update.
 */

import { isJoker, rankOf, sortCards, suitOf } from '@cards/toolkit';
import type { CardId, Suit } from '@cards/toolkit';

const SUIT_SYMBOL: Record<Suit, string> = { C: '\u2663', D: '\u2666', H: '\u2665', S: '\u2660' };
const SUIT_NAME: Record<Suit, string> = {
  C: 'clubs',
  D: 'diamonds',
  H: 'hearts',
  S: 'spades',
};
const RANK_NAME: Record<string, string> = {
  A: 'ace',
  T: 'ten',
  J: 'jack',
  Q: 'queen',
  K: 'king',
};

/**
 * A move argument rendered for a human, when we can tell what it is.
 *
 * Suits are the case that matters: a wild card asks which suit to name, and the
 * server sends "C"/"D"/"H"/"S". Knowing what a suit is, is card knowledge
 * rather than game knowledge - this client already draws them - so translating
 * here keeps the renderer generic while not showing anyone a button labelled
 * "S". Anything unrecognised is passed through untouched.
 */
export function describeArg(arg: string): { text: string; red: boolean } | null {
  const suit = SUIT_SYMBOL[arg as Suit];
  if (suit) {
    return { text: `${suit} ${SUIT_NAME[arg as Suit]}`, red: arg === 'H' || arg === 'D' };
  }
  return null;
}

/**
 * A rank as it is written on a card.
 *
 * `T` is how a ten is stored and is not how anybody says it; every other rank
 * is already its own name. The hand draws "10", so a button offering "T" beside
 * it asks the player to know the internal encoding.
 */
export function rankLabel(rank: string): string {
  return rank === 'T' ? '10' : rank;
}

/** A rank spoken in full, for screen readers: "ace", "ten", or the digit. */
export function rankSpoken(rank: string): string {
  return RANK_NAME[rank] ?? rank;
}

export interface CardFace {
  rank: string;
  suit: string;
  red: boolean;
  /** For screen readers, and for anyone who finds the pips too small. */
  label: string;
}

export function faceOf(card: CardId): CardFace {
  if (isJoker(card)) {
    return { rank: 'JKR', suit: '', red: card.charAt(1) === 'R', label: 'joker' };
  }

  const rank = rankOf(card);
  const suit = suitOf(card);
  const rankText = rank === 'T' ? '10' : (rank ?? '?');
  const suitText = suit ? SUIT_SYMBOL[suit] : '';
  const spoken = `${RANK_NAME[rank ?? ''] ?? rankText} of ${suit ? SUIT_NAME[suit] : 'unknown'}`;

  return {
    rank: rankText,
    suit: suitText,
    red: suit === 'H' || suit === 'D',
    label: spoken,
  };
}

/**
 * Sort for display only.
 *
 * The zone in state keeps its real order — reordering a hand on the server
 * would leak which card was just drawn, since a newly arrived card sorts into
 * place rather than appearing at the end.
 */
export function forDisplay(cards: readonly CardId[], sort: string | undefined): CardId[] {
  if (sort === 'suit') return sortCards(cards);
  if (sort === 'rank') {
    return cards.slice().sort((a, b) => {
      const ra = rankOf(a) ?? '';
      const rb = rankOf(b) ?? '';
      return ra.localeCompare(rb);
    });
  }
  return cards.slice();
}
